/**
 * DocumentSyncService - Centralizes document update + sync patterns
 *
 * Eliminates duplicated code across 10+ methods in App.ts that follow the pattern:
 * 1. Call api.updateDocument()
 * 2. Update sidebar
 * 3. Update storage version
 * 4. Update currentDoc reference
 */

import type { Document, DocumentMeta, ApiResponse } from './api';
import type { Sidebar } from '../components/Sidebar';
import { storage } from './storage';

export interface SyncCallbacks {
  getCurrentDoc: () => Document | null;
  setCurrentDoc: (doc: Document) => void;
  updateCurrentDocMeta: (updates: Partial<DocumentMeta>) => void;
}

export interface SyncOptions {
  /** Update storage version and remove draft */
  manageStorage?: boolean;
  /** Mark tab as saved */
  tabManager?: { setUnsaved: (id: string, unsaved: boolean) => void };
  /** Custom post-sync callback */
  onSuccess?: (doc: Document) => void | Promise<void>;
}

export class DocumentSyncService {
  constructor(
    private sidebar: Sidebar,
    private callbacks: SyncCallbacks
  ) {}

  /**
   * Sync after a successful document update
   * Handles: sidebar update, storage version, currentDoc reference
   */
  async sync(
    docId: string,
    response: ApiResponse<Document>,
    options: SyncOptions = {}
  ): Promise<boolean> {
    if (!response.success || !response.data) {
      return false;
    }

    const doc = response.data;

    // Always update sidebar
    this.sidebar.updateDocument(doc.meta);

    // Optionally manage storage (for content/title saves)
    if (options.manageStorage) {
      storage.setLocalVersion(docId, doc.meta.version);
      storage.removeDraft(docId);
    }

    // Optionally mark tab as saved
    if (options.tabManager) {
      options.tabManager.setUnsaved(docId, false);
    }

    // Update currentDoc if it's the same document
    const currentDoc = this.callbacks.getCurrentDoc();
    if (currentDoc?.meta.id === docId) {
      this.callbacks.setCurrentDoc(doc);
    }

    // Custom post-sync callback
    if (options.onSuccess) {
      await options.onSuccess(doc);
    }

    return true;
  }

  /**
   * Sync with partial meta update only (for properties like workspaceId)
   * Use when you don't want to replace the full document
   */
  syncMeta(
    docId: string,
    response: ApiResponse<Document>,
    metaUpdates: Partial<DocumentMeta>
  ): boolean {
    if (!response.success || !response.data) {
      return false;
    }

    // Update sidebar with full meta from response
    this.sidebar.updateDocument(response.data.meta);

    // Update only specific meta properties on currentDoc
    const currentDoc = this.callbacks.getCurrentDoc();
    if (currentDoc?.meta.id === docId) {
      this.callbacks.updateCurrentDocMeta(metaUpdates);
    }

    return true;
  }

  /**
   * Sync with storage version update (for favorites, etc.)
   */
  syncWithVersion(
    docId: string,
    response: ApiResponse<Document>,
    options: SyncOptions = {}
  ): boolean {
    if (!response.success || !response.data) {
      return false;
    }

    const doc = response.data;

    // Update sidebar
    this.sidebar.updateDocument(doc.meta);

    // Update storage version
    storage.setLocalVersion(docId, doc.meta.version);

    // Update currentDoc
    const currentDoc = this.callbacks.getCurrentDoc();
    if (currentDoc?.meta.id === docId) {
      this.callbacks.setCurrentDoc(doc);

      // Custom callback (e.g., updateFavoriteButton)
      if (options.onSuccess) {
        options.onSuccess(doc);
      }
    }

    return true;
  }
}
